// Samples, the worklist order and critical-result escalation (docs/plans/phase1-lab.md
// §5.5, §5.7, §6.4). Pure and deterministic: the same inputs always give the same order,
// so a list never reorders between two loads unless something changed (DESIGN.md).

import type { Result } from '../result.ts';
import { err, ok } from '../result.ts';

export const LAB_PRIORITIES = ['routine', 'urgent'] as const;
export type LabPriority = (typeof LAB_PRIORITIES)[number];

export interface CollectableOrder {
  readonly id: string;
  readonly specimenTypeCode: string;
  readonly containerCode: string;
  readonly testName: string;
}

export interface PlannedSample {
  readonly specimenTypeCode: string;
  readonly containerCode: string;
  /** In test-name order. */
  readonly orderIds: readonly string[];
}

const compareText = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);

/**
 * One sample per specimen type and container among the orders being collected together:
 * a full blood count and an ESR on the same tube type share a tube and a label. Ordered by
 * specimen, then container, so labels always print in the same order.
 */
export function groupOrdersIntoSamples(
  orders: readonly CollectableOrder[],
): Result<PlannedSample[], 'lab_sample.no_orders' | 'lab_sample.duplicate_order'> {
  if (orders.length === 0) return err('lab_sample.no_orders');
  if (new Set(orders.map((o) => o.id)).size !== orders.length) {
    return err('lab_sample.duplicate_order');
  }
  const groups = new Map<string, CollectableOrder[]>();
  for (const order of orders) {
    const key = JSON.stringify([order.specimenTypeCode, order.containerCode]);
    const group = groups.get(key);
    if (group) group.push(order);
    else groups.set(key, [order]);
  }
  return ok(
    [...groups.values()]
      .map((group) => {
        const sorted = [...group].sort(
          (a, b) => compareText(a.testName, b.testName) || compareText(a.id, b.id),
        );
        // Every order in a group has the same specimen and container.
        const [first] = sorted;
        return {
          specimenTypeCode: first?.specimenTypeCode ?? '',
          containerCode: first?.containerCode ?? '',
          orderIds: sorted.map((o) => o.id),
        };
      })
      .sort(
        (a, b) =>
          compareText(a.specimenTypeCode, b.specimenTypeCode) ||
          compareText(a.containerCode, b.containerCode),
      ),
  );
}

export interface WorklistRow {
  readonly id: string;
  /** A critical result on this row that nobody has acknowledged. */
  readonly criticalUnacknowledged: boolean;
  readonly priority: LabPriority;
  /** When the row started waiting for its current step (ordered, collected, submitted). */
  readonly waitingSince: Date;
  /** Minutes from order to release the test should take; null when not set. */
  readonly turnaroundTargetMinutes: number | null;
  readonly orderedAt: Date;
}

const MINUTE_MS = 60_000;

/** Whole minutes waited, never negative (a clock a little ahead reads 0). */
export function minutesWaiting(since: Date, now: Date): number {
  return Math.max(0, Math.floor((now.getTime() - since.getTime()) / MINUTE_MS));
}

export function isOverTurnaroundTarget(row: WorklistRow, now: Date): boolean {
  if (row.turnaroundTargetMinutes === null) return false;
  return minutesWaiting(row.orderedAt, now) > row.turnaroundTargetMinutes;
}

/**
 * Critical and unacknowledged first, then urgent, then over target, then the longest
 * wait, then by ID so equal rows keep a fixed order.
 */
export function sortWorklist<T extends WorklistRow>(rows: readonly T[], now: Date): T[] {
  const rank = (row: T) => [
    row.criticalUnacknowledged ? 0 : 1,
    row.priority === 'urgent' ? 0 : 1,
    isOverTurnaroundTarget(row, now) ? 0 : 1,
    row.waitingSince.getTime(),
  ];
  return [...rows].sort((a, b) => {
    const ra = rank(a);
    const rb = rank(b);
    for (let i = 0; i < ra.length; i += 1) {
      const d = (ra[i] ?? 0) - (rb[i] ?? 0);
      if (d !== 0) return d;
    }
    return compareText(a.id, b.id);
  });
}

export interface OpenNotification {
  readonly status: 'open' | 'acknowledged' | 'closed_by_lab';
  readonly createdAt: Date;
  readonly escalatedAt: Date | null;
  /** Null for an external request: nobody in Wodi can acknowledge, so it escalates at once. */
  readonly recipientUserId: string | null;
}

/**
 * Whether an open critical notification should escalate now (§6.4, C6). With no minutes
 * set by the clinical lead, escalation is immediate: the lab sees every unacknowledged
 * critical result straight away rather than never.
 */
export function escalationDue(
  notification: OpenNotification,
  escalationMinutes: number | null,
  now: Date,
): boolean {
  if (notification.status !== 'open' || notification.escalatedAt !== null) return false;
  if (notification.recipientUserId === null || escalationMinutes === null) return true;
  return now.getTime() - notification.createdAt.getTime() >= escalationMinutes * MINUTE_MS;
}
